import type { AcceptedApplicantData } from '@platform/client/schemas';
import type { AcceptedGroup } from '../types';

export const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export type AcceptedSortColumn = 'name' | 'project' | 'startDate' | 'endDate' | 'status';
export type SortDirection = 'asc' | 'desc' | null;

/**
 * Sort `AcceptedGroup[]` at the group level so teammates in a Teams cohort
 * stay together. Sorts by the first row's name, the matched project name,
 * the match's `projectStartDate` / `projectEndDate` (ISO strings, so
 * lexicographic order matches chronological order), or the participant
 * matchStatus. Nulls always sort last regardless of direction so
 * unmatched/discovery groups trail matched ones.
 */
export const sortAcceptedGroups = (
  groups: AcceptedGroup[],
  column: AcceptedSortColumn | null,
  direction: SortDirection,
): AcceptedGroup[] => {
  if (!column || !direction) return groups;
  const keyOf = (g: AcceptedGroup): string | null => {
    if (column === 'name') return g.rows[0]?.name ?? null;
    if (column === 'project') return g.match?.projectName ?? null;
    if (column === 'startDate') return g.match?.projectStartDate ?? null;
    if (column === 'endDate') return g.match?.projectEndDate ?? null;
    return g.matchStatus ?? null;
  };
  const dir = direction === 'asc' ? 1 : -1;
  return [...groups].sort((a, b) => {
    const ka = keyOf(a);
    const kb = keyOf(b);
    if (ka == null && kb == null) return 0;
    if (ka == null) return 1;
    if (kb == null) return -1;
    return ka.localeCompare(kb) * dir;
  });
};

/**
 * Groups accepted applicants for the AcceptedStatusTable.
 *
 * Rows sharing a `matchData.projectId` collapse into one group so the table
 * can render them as a team (one matched-project block with merged cells per
 * teammate). Rows without a `projectId` get a unique `solo-${idx}` key so
 * each unmatched/solo applicant stays in its own group. The first row's
 * `matchStatus` is carried on the group so the table can display the
 * participant lifecycle stage (e.g. "Discovery") even when no `matchData` exists.
 */
export const groupAcceptedByMatch = (rows: AcceptedApplicantData[]): AcceptedGroup[] => {
  const groups = new Map<string, AcceptedGroup>();
  rows.forEach((row, idx) => {
    const pid = row.matchData?.projectId ?? null;
    const key = pid == null ? `solo-${idx}` : `pid-${pid}`;
    const existing = groups.get(key);
    if (existing) existing.rows.push(row);
    else
      groups.set(key, {
        key,
        rows: [row],
        match: row.matchData,
        matchStatus: row.matchStatus,
      });
  });
  return [...groups.values()];
};
